import DoneAllIcon from '@mui/icons-material/DoneAll';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CallIcon from '@mui/icons-material/Phone';
import { Box, Divider, Grid, Modal, Paper, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { getCallLogTranscript } from 'wisestep-api';
import { WsButton, WsColors, WsTypoColors, WsTypoVariants, WsTypography } from 'wisestep-base';

interface IUser {
  username: string;
  multiple_people: boolean;
  user_id: string;
  zoom_userid: string;
  client_type: number;
}

interface ITimelineEntry {
  text: string;
  raw_text: string;
  end_ts: string;
  ts: string;
  users: IUser[];
}

interface ITranscript {
  type: string;
  ver: number;
  recording_id: string;
  meeting_id: string;
  account_id: string;
  host_id: string;
  recording_start: string;
  recording_end: string;
  timeline: ITimelineEntry[];
}

const CallLogCard = ({ callLog }) => {
  const {
    direction,
    service,
    recruiterName,
    createdOn,
    duration,
    wisestepStatus,
    transcriptURL,
    accountId,
  } = callLog;
  const [transcript, setTranscript] = useState<ITranscript | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  const formatDate = (date) => {
    const logDate = new Date(date);
    const today = new Date();

    if (
      logDate.getDate() === today.getDate() &&
      logDate.getMonth() === today.getMonth() &&
      logDate.getFullYear() === today.getFullYear()
    ) {
      return logDate.toLocaleTimeString();
    } else {
      return logDate.toLocaleDateString();
    }
  };

  const handleViewTranscript = async () => {
    try {
      const response = await getCallLogTranscript(transcriptURL, accountId);
      setTranscript(response.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Error fetching transcript:', error);
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return '';

    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const getStatusColor = (status, textflag) => {
    if (textflag === 1) {
      if (status === 'Call Connected') {
        return WsTypoColors.PRIMARY;
      } else {
        return WsTypoColors.ERROR;
      }
    } else {
      if (status === 'Call Connected') {
        return WsColors.TheBlues100;
      } else {
        return WsColors.Red100;
      }
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          <CallIcon color="primary" />
          <Box sx={{ ml: 1 }}>
            <WsTypography variant={WsTypoVariants.BODY1}>
              {direction === 'INBOUND' ? 'Incoming Call' : 'Outgoing Call'}
            </WsTypography>
          </Box>
          <Box sx={{ ml: 3 }}>
            <WsTypography variant={WsTypoVariants.BODY1}>{service}</WsTypography>
          </Box>
          <Box ml={'auto'}>
            <WsTypography variant={WsTypoVariants.BODY1}>
              {duration ? `Duration - ${formatDuration(duration)}` : ''}
            </WsTypography>
          </Box>
          <Box
            sx={{ ml: 2, alignItems: 'center' }}
            height={30}
            textAlign="center"
            p={0.8}
            borderRadius={2}
            bgcolor={getStatusColor(wisestepStatus, 0)}
          >
            <WsTypography
              variant={WsTypoVariants.BODY2}
              color={getStatusColor(wisestepStatus, 1) as WsTypoColors | undefined}
            >
              {wisestepStatus === 'Call Connected' && (
                <DoneAllIcon sx={{ fontSize: 14, marginRight: 1 }} />
              )}
              {wisestepStatus}
            </WsTypography>
          </Box>
        </Box>
        <Box sx={{ ml: 2, mt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FiberManualRecordIcon sx={{ fontSize: 'small' }} />
            <Box sx={{ ml: 3 }}>
              <WsTypography variant={WsTypoVariants.BODY2}>
                Spoken to: {recruiterName} at {formatDate(createdOn)}{' '}
              </WsTypography>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1,
              ml: 'auto',
              display: 'block',
            }}
          >
            <WsButton
              variant="outlined"
              disabled={wisestepStatus !== 'Call Connected' || !transcriptURL}
              onClick={handleViewTranscript}
            >
              View Transcript
            </WsButton>

            <Modal
              open={isModalOpen}
              onClose={() => setModalOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                style={{
                  width: '50%',
                  backgroundColor: 'white',
                  padding: '20px',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                }}
                sx={{ position: 'relative' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: '10px',
                    marginRight: 'auto',
                  }}
                >
                  <WsTypography variant={WsTypoVariants.TITLE}>Call Transcript</WsTypography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: '#ccc',
                    marginY: '10px',
                  }}
                />
                {transcript && transcript.timeline && (
                  <Stack spacing={2}>
                    {transcript.timeline.map((entry, index) => (
                      <Grid key={index}>
                        <Stack direction="row" alignItems="center">
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 'small',
                              marginRight: '1rem',
                              color: WsColors.TheBlues200,
                            }}
                          />
                          <Stack direction="column" spacing={1}>
                            {entry.users.length > 0 && (
                              <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                                {entry.users.map((user, userIndex) => (
                                  <React.Fragment key={userIndex}>
                                    {user.username}
                                    {userIndex < entry.users.length - 1 && ', '}
                                  </React.Fragment>
                                ))}
                              </WsTypography>
                            )}
                            <Typography variant={WsTypoVariants.BODY1}>{entry.text}</Typography>
                          </Stack>
                        </Stack>
                        <Divider orientation="vertical" flexItem />
                      </Grid>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Modal>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CallLogCard;
